/** Flota — widoki analityki (`fleet.web.an.*`). Typowane en względem pl. */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const fleetAnalyticsViewsPl = {
  an: {
    title: 'Analityka floty',
    description: 'Przebieg z odczytów licznika, zużycie paliwa, koszty i limity leasingu — z jawnym trybem akceptacji.',
    tabs: { mileage: 'Przebieg', fuel: 'Zużycie paliwa', costs: 'Koszty', leases: 'Limity leasingu', vehicles: 'Porównanie pojazdów', periods: 'Porównanie okresów', trend: 'Trend' },
    loadFailed: 'Nie udało się wczytać analityki.',
    empty: 'Brak danych dla wybranych filtrów.',
    noData: 'brak danych',
    filters: {
      period: 'Okres',
      from: 'Od',
      to: 'Do',
      presets: { '30d': '30 dni', '90d': '90 dni', month: 'Ten miesiąc', prevMonth: 'Poprzedni miesiąc', '12m': '12 miesięcy', ytd: 'Od początku roku' },
      vehicle: 'Pojazd',
      allVehicles: 'Wszystkie pojazdy',
      driver: 'Kierowca',
      allDrivers: 'Wszyscy kierowcy',
      status: 'Status pojazdu',
      allStatuses: 'Wszystkie statusy',
      type: 'Typ pojazdu',
      allTypes: 'Wszystkie typy',
      category: 'Kategoria kosztu',
      allCategories: 'Wszystkie kategorie',
      unapproved: 'Uwzględnij niezaakceptowane',
      unapprovedHint: 'Domyślnie liczą się tylko zaakceptowane wpisy. Włączenie dolicza wpisy wysłane i do uzupełnienia (nigdy odrzucone).',
      rangeInvalid: 'Data początku jest późniejsza niż data końca.',
      rangeTooLarge: 'Okres jest zbyt długi — maksymalnie 1830 dni.',
      reset: 'Wyczyść filtry',
    },
    categories: { FUEL: 'Paliwo', LEASE: 'Leasing', SERVICE: 'Serwis' },
    mode: {
      APPROVED_ONLY: 'Tryb: tylko zaakceptowane wpisy',
      INCLUDING_UNAPPROVED: 'Tryb: wraz z niezaakceptowanymi (wynik wstępny)',
    },
    export: 'Eksport Excel',
    exportDone: 'Eksport gotowy.',
    unapprovedWarning: 'Wynik nie zawiera {amount} ({count} pozycji) czekających na weryfikację.',
    unapprovedWarningCount: 'Wynik nie zawiera {count} pozycji czekających na weryfikację.',
    unapprovedInclude: 'Uwzględnij je',
    totals: 'Razem',
    vehicle: 'Pojazd',
    km: 'Km',
    liters: 'Litry',
    mileage: {
      kmTotal: 'Przebieg (licznik)',
      logbookKm: 'Km z kart drogowych',
      undocumentedKm: 'Km bez karty drogowej',
      vehicles: 'Pojazdy',
      baselineMissing: 'Pojazdy bez odczytu bazowego',
      granularity: { auto: 'Auto', day: 'Dziennie', month: 'Miesięcznie' },
      chartKm: 'Przebieg w okresie',
      chartCumulative: 'Narastająco',
      byVehicle: 'Przebieg według pojazdów',
      col: { odometer: 'Licznik', logbook: 'Karty', undocumented: 'Bez karty', readings: 'Odczyty', meter: 'Korekty licznika' },
      kmSourceLogbook: 'Filtr kierowcy: licznik nie zna kierowcy, więc przebieg to suma km z kart drogowych kierowcy zakończonych w okresie.',
      kmSourceOdometer: 'Przebieg liczony wyłącznie z odczytów licznika — nigdy z km planowanych.',
      baselineHint: 'Brak odczytu sprzed okresu — przebieg liczony od pierwszego odczytu w okresie i może być zaniżony.',
      noReadings: 'brak odczytów',
      baselineBadge: 'może być zaniżony',
      dayTooLong: 'Widok dzienny jest dostępny do 400 dni — wyświetlono miesięczny.',
    },
    fuel: {
      byVehicle: 'Zużycie paliwa według pojazdów',
      explain: 'Metoda „pełny do pełnego”: zużycie to litry między dwoma kolejnymi pełnymi tankowaniami podzielone przez przejechane km. Wynik pokazujemy tylko wtedy, gdy wszystkie odcinki w okresie są kompletne.',
      col: { consumption: 'Zużycie', liters: 'Litry', km: 'Km (odcinki kompletne)', segments: 'Odcinki', fuel: 'Paliwo', unclosed: 'Niezamknięte litry' },
      unit: 'l/100 km',
      incomplete: 'Niekompletne dane',
      indicative: 'orientacyjnie ≈ {value} l/100 km (nie jest wynikiem)',
      noIndicative: 'bez wartości orientacyjnej',
      segments: '{ok} kompletnych, {bad} niekompletnych',
      showSegments: 'Odcinki',
      hideSegments: 'Ukryj odcinki',
      segmentFrom: 'Od',
      segmentTo: 'Do',
      segmentStatus: { OK: 'Kompletny', INCOMPLETE: 'Niekompletny' },
      unclosedHint: 'Litry po ostatnim pełnym tankowaniu — jeszcze nie wchodzą do wyniku.',
      okCount: 'Pojazdy z wynikiem',
      incompleteCount: 'Pojazdy niekompletne',
      chart: 'Zużycie — pojazdy z kompletnymi danymi',
      reasons: {
        NOT_ENOUGH_FULL_TANKS: 'Za mało pełnych tankowań',
        ODOMETER_MISSING: 'Brak stanu licznika przy tankowaniu',
        ODOMETER_INCONSISTENT: 'Stan licznika niespójny (nie rośnie)',
        DISTANCE_TOO_SHORT: 'Odcinek krótszy niż 100 km',
        METER_CORRECTION_IN_WINDOW: 'Korekta lub wymiana licznika w odcinku',
        UNVERIFIED_ENTRY_IN_SEGMENT: 'Tankowanie czeka na weryfikację',
        MIXED_FUEL_TYPES: 'Różne rodzaje paliwa w odcinku',
        FUEL_TYPE_NOT_SUPPORTED: 'Rodzaj paliwa nieobsługiwany (prąd, AdBlue)',
        IMPLAUSIBLE_VALUE: 'Wynik poza wiarygodnym zakresem 2–100 l/100 km',
      },
    },
    costs: {
      total: 'Koszt razem',
      perKm: 'Koszt na km',
      fuel: 'Paliwo',
      lease: 'Leasing',
      other: 'Pozostałe',
      km: 'Przebieg (mianownik)',
      included: 'Uwzględnione kategorie',
      byVehicle: 'Koszty według pojazdów',
      byCategory: 'Koszty według kategorii',
      share: 'Udział',
      count: 'Pozycji',
      chart: 'Struktura kosztów',
      chartVehicles: 'Koszt według pojazdów (najwyższe)',
      notes: {
        NO_DISTANCE: 'Brak przebiegu z licznika w okresie — koszt na km niedostępny (kwoty są podane).',
        PARTIAL_DISTANCE:
          'Część pojazdów nie ma przebiegu z licznika — koszt na km jest orientacyjny: liczony tylko dla pojazdów z przebiegiem (kwoty obejmują wszystkie).',
        LEASE_FEE_UNKNOWN: 'Dla części umów nie podano raty — koszt leasingu jest niepełny.',
        BASELINE_MISSING: 'Brak odczytu bazowego — przebieg (i koszt na km) może być zawyżony lub zaniżony.',
        FOREIGN_CURRENCY_EXCLUDED: 'Koszty w walucie innej niż PLN nie są wliczone.',
      },
      leaseFeeUnknown: 'rata nieznana',
      perKmUnavailable: 'brak przebiegu',
    },
    leases: {
      description: 'Te same obliczenia co karta umowy. Prognoza z małej ilości danych jest oznaczona jako orientacyjna.',
      col: { contract: 'Umowa', usage: 'Wykorzystanie limitu', remaining: 'Pozostało / przekroczenie', forecast: 'Prognoza przekroczenia', ends: 'Koniec umowy' },
      remaining: 'zostało {km}',
      exceeded: 'ponad limit o {km}',
      noForecast: 'brak prognozy',
      forecastCost: 'koszt {amount}',
      noRate: 'koszt nieznany (brak stawki)',
      asOf: 'Stan na {date}',
      empty: 'Brak aktywnych umów z limitem km dla wybranych filtrów.',
    },
    vehicles: {
      description: 'Pojazdy obok siebie w wybranym okresie. Kliknij nagłówek kolumny, aby sortować.',
      col: { consumption: 'Zużycie l/100 km', fuelCost: 'Paliwo', fuelPerKm: 'Paliwo / km', total: 'Koszt razem', perKm: 'Koszt / km', lease: 'Limit leasingu' },
      chart: 'Koszt na km według pojazdów',
    },
    periods: {
      description: 'Okres bieżący a porównawczy (domyślnie poprzedni okres o tej samej długości).',
      compareFrom: 'Porównaj od',
      compareTo: 'Porównaj do',
      useDefault: 'Poprzedni okres',
      current: 'Bieżący',
      previous: 'Porównawczy',
      delta: 'Zmiana',
      metrics: { km: 'Przebieg', liters: 'Litry', fuelCost: 'Koszt paliwa', leaseCost: 'Koszt leasingu', otherCost: 'Koszty pozostałe', totalCost: 'Koszt razem', totalCostPerKm: 'Koszt na km', lPer100: 'Zużycie l/100 km' },
      unknown: 'nieznana',
      noBase: 'brak bazy porównawczej',
      incompleteNote: 'Zużycie jest podane tylko dla okresów z kompletnymi danymi.',
    },
    trend: {
      description: 'Szereg miesięczny z ostatnich miesięcy (włącznie z bieżącym; ignoruje wybrany okres).',
      months: 'Liczba miesięcy',
      month: 'Miesiąc',
      metric: 'Wskaźnik',
      metrics: { km: 'Przebieg (km)', liters: 'Litry', fuelCost: 'Koszt paliwa', totalCost: 'Koszt razem', totalCostPerKm: 'Koszt na km' },
      noValue: 'brak danych w miesiącu',
    },
  },
};

export const fleetAnalyticsViewsEn: Shape<typeof fleetAnalyticsViewsPl> = {
  an: {
    title: 'Fleet analytics',
    description: 'Mileage from odometer readings, fuel consumption, costs and lease limits — with an explicit acceptance mode.',
    tabs: { mileage: 'Mileage', fuel: 'Fuel consumption', costs: 'Costs', leases: 'Lease limits', vehicles: 'Vehicle comparison', periods: 'Period comparison', trend: 'Trend' },
    loadFailed: 'Could not load analytics.',
    empty: 'No data for the selected filters.',
    noData: 'no data',
    filters: {
      period: 'Period',
      from: 'From',
      to: 'To',
      presets: { '30d': '30 days', '90d': '90 days', month: 'This month', prevMonth: 'Previous month', '12m': '12 months', ytd: 'Year to date' },
      vehicle: 'Vehicle',
      allVehicles: 'All vehicles',
      driver: 'Driver',
      allDrivers: 'All drivers',
      status: 'Vehicle status',
      allStatuses: 'All statuses',
      type: 'Vehicle type',
      allTypes: 'All types',
      category: 'Cost category',
      allCategories: 'All categories',
      unapproved: 'Include unapproved',
      unapprovedHint: 'By default only approved entries count. Turning this on adds submitted and needs-info entries (never rejected).',
      rangeInvalid: 'The start date is later than the end date.',
      rangeTooLarge: 'The period is too long — 1830 days at most.',
      reset: 'Clear filters',
    },
    categories: { FUEL: 'Fuel', LEASE: 'Lease', SERVICE: 'Service' },
    mode: {
      APPROVED_ONLY: 'Mode: approved entries only',
      INCLUDING_UNAPPROVED: 'Mode: including unapproved (preliminary result)',
    },
    export: 'Export to Excel',
    exportDone: 'Export ready.',
    unapprovedWarning: 'The result excludes {amount} ({count} items) awaiting review.',
    unapprovedWarningCount: 'The result excludes {count} items awaiting review.',
    unapprovedInclude: 'Include them',
    totals: 'Total',
    vehicle: 'Vehicle',
    km: 'Km',
    liters: 'Litres',
    mileage: {
      kmTotal: 'Mileage (odometer)',
      logbookKm: 'Km from logbooks',
      undocumentedKm: 'Km without a logbook',
      vehicles: 'Vehicles',
      baselineMissing: 'Vehicles without a baseline reading',
      granularity: { auto: 'Auto', day: 'Daily', month: 'Monthly' },
      chartKm: 'Mileage in the period',
      chartCumulative: 'Cumulative',
      byVehicle: 'Mileage by vehicle',
      col: { odometer: 'Odometer', logbook: 'Logbooks', undocumented: 'No logbook', readings: 'Readings', meter: 'Meter events' },
      kmSourceLogbook: 'Driver filter: the odometer does not know the driver, so mileage is the sum of the driver’s logbook km finished in the period.',
      kmSourceOdometer: 'Mileage is computed from odometer readings only — never from planned km.',
      baselineHint: 'No reading before the period — mileage is counted from the first reading in the period and may be understated.',
      noReadings: 'no readings',
      baselineBadge: 'may be understated',
      dayTooLong: 'The daily view is available up to 400 days — the monthly view is shown.',
    },
    fuel: {
      byVehicle: 'Fuel consumption by vehicle',
      explain: 'Full-to-full method: consumption is the litres between two consecutive full fill-ups divided by the km driven. A figure is shown only when every segment in the period is complete.',
      col: { consumption: 'Consumption', liters: 'Litres', km: 'Km (complete segments)', segments: 'Segments', fuel: 'Fuel', unclosed: 'Unclosed litres' },
      unit: 'l/100 km',
      incomplete: 'Incomplete data',
      indicative: 'indicative ≈ {value} l/100 km (not a result)',
      noIndicative: 'no indicative value',
      segments: '{ok} complete, {bad} incomplete',
      showSegments: 'Segments',
      hideSegments: 'Hide segments',
      segmentFrom: 'From',
      segmentTo: 'To',
      segmentStatus: { OK: 'Complete', INCOMPLETE: 'Incomplete' },
      unclosedHint: 'Litres after the last full fill-up — not part of the result yet.',
      okCount: 'Vehicles with a result',
      incompleteCount: 'Incomplete vehicles',
      chart: 'Consumption — vehicles with complete data',
      reasons: {
        NOT_ENOUGH_FULL_TANKS: 'Not enough full fill-ups',
        ODOMETER_MISSING: 'Odometer missing at a fill-up',
        ODOMETER_INCONSISTENT: 'Inconsistent odometer (not increasing)',
        DISTANCE_TOO_SHORT: 'Segment shorter than 100 km',
        METER_CORRECTION_IN_WINDOW: 'Odometer correction or replacement in the segment',
        UNVERIFIED_ENTRY_IN_SEGMENT: 'A fill-up is awaiting review',
        MIXED_FUEL_TYPES: 'Different fuel types in the segment',
        FUEL_TYPE_NOT_SUPPORTED: 'Fuel type not supported (electric, AdBlue)',
        IMPLAUSIBLE_VALUE: 'Result outside the plausible 2–100 l/100 km range',
      },
    },
    costs: {
      total: 'Total cost',
      perKm: 'Cost per km',
      fuel: 'Fuel',
      lease: 'Lease',
      other: 'Other',
      km: 'Mileage (denominator)',
      included: 'Included categories',
      byVehicle: 'Costs by vehicle',
      byCategory: 'Costs by category',
      share: 'Share',
      count: 'Items',
      chart: 'Cost structure',
      chartVehicles: 'Cost by vehicle (highest)',
      notes: {
        NO_DISTANCE: 'No odometer mileage in the period — cost per km is unavailable (amounts are shown).',
        PARTIAL_DISTANCE:
          'Some vehicles have no odometer mileage — cost per km is indicative: computed only for vehicles with mileage (amounts cover all vehicles).',
        LEASE_FEE_UNKNOWN: 'Some contracts have no instalment recorded — the lease cost is incomplete.',
        BASELINE_MISSING: 'No baseline reading — mileage (and cost per km) may be off.',
        FOREIGN_CURRENCY_EXCLUDED: 'Costs in a currency other than PLN are not included.',
      },
      leaseFeeUnknown: 'instalment unknown',
      perKmUnavailable: 'no mileage',
    },
    leases: {
      description: 'The same calculations as the contract page. A forecast based on little data is marked as indicative.',
      col: { contract: 'Contract', usage: 'Limit usage', remaining: 'Remaining / over', forecast: 'Forecast overage', ends: 'Contract end' },
      remaining: '{km} left',
      exceeded: 'over the limit by {km}',
      noForecast: 'no forecast',
      forecastCost: 'cost {amount}',
      noRate: 'cost unknown (no rate)',
      asOf: 'As of {date}',
      empty: 'No active contracts with a km limit for the selected filters.',
    },
    vehicles: {
      description: 'Vehicles side by side in the selected period. Click a column header to sort.',
      col: { consumption: 'Consumption l/100 km', fuelCost: 'Fuel', fuelPerKm: 'Fuel / km', total: 'Total cost', perKm: 'Cost / km', lease: 'Lease limit' },
      chart: 'Cost per km by vehicle',
    },
    periods: {
      description: 'Current vs comparison period (by default the previous period of the same length).',
      compareFrom: 'Compare from',
      compareTo: 'Compare to',
      useDefault: 'Previous period',
      current: 'Current',
      previous: 'Comparison',
      delta: 'Change',
      metrics: { km: 'Mileage', liters: 'Litres', fuelCost: 'Fuel cost', leaseCost: 'Lease cost', otherCost: 'Other costs', totalCost: 'Total cost', totalCostPerKm: 'Cost per km', lPer100: 'Consumption l/100 km' },
      unknown: 'unknown',
      noBase: 'no comparison base',
      incompleteNote: 'Consumption is shown only for periods with complete data.',
    },
    trend: {
      description: 'Monthly series for the last months (including the current one; ignores the selected period).',
      months: 'Number of months',
      month: 'Month',
      metric: 'Metric',
      metrics: { km: 'Mileage (km)', liters: 'Litres', fuelCost: 'Fuel cost', totalCost: 'Total cost', totalCostPerKm: 'Cost per km' },
      noValue: 'no data in the month',
    },
  },
};
